<script lang="ts">
  import FormWrapper, { type FormContext } from "@kontextwerk/web-sdk/ui/layout/FormWrapper.svelte"
  import { validateField, validateInput } from "@kontextwerk/web-sdk/core/forms/formValidation"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import PropertyForm, {
    type PropertyFormModel,
  } from "../../../../../../_shared/_modules/property-form"
  import {
    confirmSimilarAddress,
    normalizeAddressInput,
  } from "../../../../../../_shared/_logic/address-duplicate-guard"
  import { normalizeDachCountrySelection } from "../../../../../../../../../../../../_shared/_logic/countries"

  interface Props {
    property: Property | null
    organizationId?: string
    loading?: boolean
    error?: string | null
    saving?: boolean
    canEdit?: boolean
    onRetry?: () => void
    onSubmit?: (payload: PropertyUpdatePayload) => Promise<void> | void
  }

  let {
    property = null,
    organizationId = "",
    loading = false,
    error = null,
    saving = false,
    canEdit = true,
    onRetry = () => {},
    onSubmit = async () => {},
  }: Props = $props()

  const fieldIds: Record<keyof PropertyFormModel, string> = {
    street: "property-street",
    house_number: "property-house-number",
    zip: "property-zip",
    city: "property-city",
    country: "property-country",
    address_extra: "property-address-extra",
    geo_lat: "property-geo-lat",
    geo_lng: "property-geo-lng",
  }

  const parseOptionalNumber = (value: string | number | null | undefined) => {
    if (value === null || value === undefined) return null

    const trimmed = `${value}`.trim()

    if (!trimmed) return null

    const parsed = Number(trimmed)

    return Number.isFinite(parsed) ? parsed : null
  }

  const normalizeString = (value: string | null | undefined) => (value ?? "").trim()

  const getInitObject = (): PropertyFormModel => {
    return {
      street: property?.street ?? "",
      house_number: property?.house_number ?? "",
      zip: property?.zip ?? "",
      city: property?.city ?? "",
      country: normalizeDachCountrySelection(property?.country),
      address_extra: property?.address_extra ?? "",
      geo_lat:
        property?.geo_lat !== null && property?.geo_lat !== undefined
          ? String(property.geo_lat)
          : "",
      geo_lng:
        property?.geo_lng !== null && property?.geo_lng !== undefined
          ? String(property.geo_lng)
          : "",
    }
  }

  const formKey = $derived(
    JSON.stringify([
      property?.id ?? "new",
      property?.street ?? "",
      property?.house_number ?? "",
      property?.zip ?? "",
      property?.city ?? "",
      property?.country ?? "",
      property?.address_extra ?? "",
      property?.geo_lat ?? "",
      property?.geo_lng ?? "",
      property?.units?.length ?? 0,
    ]),
  )

  const hasPropertyChanges = (data: PropertyFormModel, target: Property | null) =>
    Boolean(
      target &&
      (normalizeString(data.street) !== normalizeString(target.street) ||
        normalizeString(data.house_number) !== normalizeString(target.house_number) ||
        normalizeString(data.zip) !== normalizeString(target.zip) ||
        normalizeString(data.city) !== normalizeString(target.city) ||
        normalizeString(data.country) !== normalizeString(target.country) ||
        normalizeString(data.address_extra) !== normalizeString(target.address_extra) ||
        parseOptionalNumber(data.geo_lat) !== target.geo_lat ||
        parseOptionalNumber(data.geo_lng) !== target.geo_lng),
    )

  const validateAddress = ({ data }: { data: PropertyFormModel; form: HTMLFormElement | null }) => {
    const required = [
      { id: fieldIds.street, value: data.street },
      { id: fieldIds.city, value: data.city },
      { id: fieldIds.country, value: data.country },
    ]
    let isValid = true
    for (const entry of required) {
      if (!validateField(entry.id, entry.value, validateInput)) {
        isValid = false
      }
    }

    return isValid
  }

  const submitForm = async (data: PropertyFormModel) => {
    if (!property || !onSubmit || saving || !canEdit) return

    const normalizedAddress = normalizeAddressInput({
      street: data.street,
      house_number: data.house_number,
      zip: data.zip,
      city: data.city,
      country: data.country,
    })
    const normalizedData: PropertyFormModel = {
      ...data,
      ...normalizedAddress,
      house_number: normalizedAddress.house_number,
    }

    if (!hasPropertyChanges(normalizedData, property)) {
      return
    }

    const resolvedOrganizationId = organizationId || property?.organization_id || ""

    if (resolvedOrganizationId) {
      const approved = await confirmSimilarAddress({
        organizationId: resolvedOrganizationId,
        address: normalizedAddress,
        excludePropertyId: property?.id ?? null,
        approvalLabel: "Trotzdem ändern",
      })

      if (!approved) return
    }

    const payload: PropertyUpdatePayload = {
      street: normalizedAddress.street || property.street,
      house_number: normalizedAddress.house_number || null,
      zip: normalizedAddress.zip || null,
      city: normalizedAddress.city || null,
      country: normalizedAddress.country || null,
      address_extra: normalizeString(data.address_extra) || null,
      geo_lat: parseOptionalNumber(data.geo_lat),
      geo_lng: parseOptionalNumber(data.geo_lng),
    }
    await onSubmit(payload)
  }

  const saveAfterBlur = (event: FocusEvent, ctx: FormContext) => {
    if (!canEdit) return

    const target = event.currentTarget as HTMLElement | null
    const relatedTarget = event.relatedTarget as Node | null

    if (target && relatedTarget && target.contains(relatedTarget)) return

    void ctx.submit()
  }
</script>

<div class="details-grid">
  {#key formKey}
    <FormWrapper
      theme="dark"
      mode="edit"
      {getInitObject}
      validateData={validateAddress}
      onSubmit={submitForm}
      showReset={false}
      hideSubmit={true}
      formSnippet={formContent}
    />
  {/key}
</div>

{#snippet formContent(ctx: FormContext)}
  {@const { data, updateData, submitting, loading: formLoading } = ctx}
  <div onfocusout={(event) => saveAfterBlur(event, ctx)}>
    <PropertyForm
      {data}
      {updateData}
      disabled={!canEdit || saving || submitting || formLoading}
      {fieldIds}
    />
  </div>
{/snippet}

<style lang="less">
  @import "@kontextwerk/web-sdk/theme/tokens.less";

  .details-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1rem;
  }
</style>
